import React, { useMemo, useContext } from 'react';
import Price from 'components/Price';
import { getItemImage, getItemPrices } from 'utils/item';
import { getMerchantImage } from 'utils/merchant';
import useFetchedData from 'hooks/useFetchedData';
import { AddressContext } from 'constants/contexts';
import { useDebounce } from 'use-debounce';
import ConfigProvider from 'components/ConfigProvider';
import { env } from 'constants';
import { Groups } from './constants';

function useFetchedOptions(query) {
  const { currentAddress } = useContext(AddressContext);

  const [search] = useDebounce(query, 500);
  const queryValid = search && search.length > 2;
  const { data: items, loading: itemsLoading } = useFetchedData(
    queryValid && {
      url: 'v1/items/nearby/',
      query: {
        query:
          '{id,item_types,name,images,description,restaurant,restaurant_name,parent_slug,discount}',
        restaurant__owner__onboarded: 'True',
        is_active: 'True',
        available: 'True',
        restaurant__parent__organization: ConfigProvider.ORG_ID,
        lat: currentAddress?.lat,
        lng: currentAddress?.lng,
        search,
        page: 1,
      },
    },
    ({ data } = {}) => data,
  );

  const { data: merchants, loading: merchantsLoading } = useFetchedData(queryValid && {
    url: 'v1/restaurants/',
    query: {
      query: '{id,logo,name,images,rating,address,onboarded,parent{delivery_fee},tags,takeout,scheduling_available_to,has_scheduling_order}',
      restaurant__owner__onboarded: 'True',
      is_disabled: 'False',
      available: 'True',
      lat: currentAddress?.lat,
      lng: currentAddress?.lng,
      search,
      page: 1,
      is_catering: env.IS_CATERING ? 'True' : 'False',
    },
  });

  const options = useMemo(() => ([
    ...(items ? items.results.slice(0, 3).map(({ name, ...i }) => ({
      ...i,
      image: getItemImage(i),
      description: <Price {...getItemPrices({ item: i })} horizontal showPercent />,
      label: name,
      group: Groups.ITEMS,
    })) : []),
    ...(merchants ? merchants.results.slice(0, 3).map(({
      tags, name, ...m
    }) => ({
      ...m,
      image: getMerchantImage(m),
      label: name,
      description: tags.map((t) => t.title).join(', '),
      group: Groups.MERCHANTS,
    })) : []),
  ]), [items, merchants]);

  return {
    options,
    loading: itemsLoading || merchantsLoading,
    count: {
      [Groups.ITEMS]: items?.count,
      [Groups.MERCHANTS]: merchants?.count,
    },
  };
}

export default useFetchedOptions;
